import React from 'react';

import Logo from 'App/components/Logo';
import { useIsImpersonated, useLogout, useUserPages } from 'Auth';
import clsx from 'clsx';

import {
  AppBar,
  makeStyles,
  Stack,
  IconButton,
  LogoutIcon,
  PermMediaIcon,
  QueryStatsIcon,
  LightModeIcon,
  DarkModeOutlinedIcon,
  DARK_THEME_ID,
  LIGHT_THEME_ID,
  useTheme,
  useThemeSwitcher,
} from '@gogame/components';
import { ScreenLink, useLocation } from '@gogame/screens';
import { permission as TrackerPermission } from 'scenes/Trackers/Tracker/constants';
import { permission as GalleryPermission } from 'scenes/Gallery/GalleryPage/constants';
import { galleryLink } from 'scenes/Gallery/GalleryPage/navigation';
import { trackerLink } from 'scenes/Trackers/Tracker/navigation';
import { getDefaultPartnersRoute } from 'scenes/partnerRoutes';

const useStyles = makeStyles(
  (theme) => {
    return {
      container: {
        width: '64px',
        height: '100vh',
      },
      impersonated: {
        background: theme.palette.secondary.main,
        color: theme.palette.text.secondary,
      },
      normal: {
        background: theme.palette.background.paper,
        color: theme.palette.text.secondary,
      },
      appBar: {
        width: '64px',
        height: '100%',
        left: '0',
        right: 'auto',
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: '0!important',
        zIndex: theme.zIndex.drawer + 1,
      },
      logo: {
        height: '64px',
        width: '64px',
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      bottom: {
        paddingBottom: '20px',
      },
      active: {
        background: `${theme.palette.action.hover}`,
      },
    };
  },
  { name: 'GgSidebarToolbar' }
);

export const PartnersSidebarToolbar = () => {
  const classes = useStyles();
  const impersonated = useIsImpersonated();
  const { setActiveThemeId } = useThemeSwitcher();

  const pages = useUserPages();
  const defaultRoute = getDefaultPartnersRoute(pages);

  const logout = useLogout();
  const location = useLocation();
  const theme = useTheme();

  const handleSetDarkTheme = () => {
    setActiveThemeId(DARK_THEME_ID);
  };

  const handleSetLightTheme = () => {
    setActiveThemeId(LIGHT_THEME_ID);
  };

  const isAllowTracker = pages.includes(TrackerPermission);
  const isAllowGallery = pages.includes(GalleryPermission);
  return (
    <>
      <AppBar
        position={'fixed'}
        className={clsx(classes.appBar, {
          [classes.impersonated]: impersonated,
          [classes.normal]: !impersonated,
        })}
        elevation={0}
        data-cy={'screenSidebar'}
      >
        <Stack spacing={1} alignItems={'center'} grow={1}>
          <div className={clsx(classes.logo)}>
            <ScreenLink link={defaultRoute}>
              <Logo impersonated={impersonated} />
            </ScreenLink>
          </div>

          <React.Fragment>
            {isAllowTracker ? (
              <ScreenLink link={trackerLink} fast={true}>
                <IconButton
                  icon={<QueryStatsIcon />}
                  tooltip={'Tracker'}
                  tooltipPlacement={'right'}
                  className={{
                    [classes.active]: location.pathname.includes(
                      trackerLink.path
                    ),
                  }}
                />
              </ScreenLink>
            ) : null}

            {isAllowGallery ? (
              <ScreenLink link={galleryLink} fast={true}>
                <IconButton
                  icon={<PermMediaIcon />}
                  tooltip={'Gallery'}
                  tooltipPlacement={'right'}
                  className={{
                    [classes.active]: location.pathname.includes(
                      galleryLink.path
                    ),
                  }}
                />
              </ScreenLink>
            ) : null}
          </React.Fragment>
        </Stack>

        <Stack spacing={1} alignItems={'center'} className={classes.bottom}>
          {theme.palette.mode === LIGHT_THEME_ID ? (
            <IconButton
              tooltip={'Set dark theme'}
              tooltipPlacement={'right'}
              icon={<DarkModeOutlinedIcon />}
              onClick={handleSetDarkTheme}
            />
          ) : null}
          {theme.palette.mode === DARK_THEME_ID ? (
            <IconButton
              tooltip={'Set light theme'}
              tooltipPlacement={'right'}
              icon={<LightModeIcon />}
              onClick={handleSetLightTheme}
            />
          ) : null}
          <IconButton
            tooltip={'Logout'}
            tooltipPlacement={'right'}
            icon={<LogoutIcon />}
            onClick={logout}
          />
        </Stack>
      </AppBar>
    </>
  );
};
